// src/components/Login.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { loginUser } from "../redux/slices/branch/authSlice";
import "../assets/css/login.css";
import axios from "axios";
import { parse } from "tldts";
import { toast } from "react-toastify";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [company, setCompany] = useState({
    name: "",
    logo: null,
    favicon: null,
    domain: "",
    subdomain: "",
  });
  const [fetchError, setFetchError] = useState("");

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ===== Utility to get domain and subdomain =====
  const getDomainInfo = () => {
    const hostname = window.location.hostname;
    const parsed = parse(hostname);
    const domain = parsed.domainWithoutSuffix || "";
    const subdomain = parsed.subdomain || "";
    return { domain, subdomain };
  };

  // ===== Utility to build full image URL =====
  const getImageUrl = (path) => {
    if (!path) return "";
    const cleanPath = path.startsWith("images/") ? path.slice(7) : path;
    return `https://narrowitsolution.in/${cleanPath}`;
  };

  // ===== Fetch company info based on domain =====
  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const { domain } = getDomainInfo();
        const response = await axios.get("https://narrowitsolution.in/api/get-company", {
          params: { domain },
        });
        const data = response.data;

        if (data && Object.keys(data).length > 0) {
          setCompany({
            name: data.name || "",
            logo: data.logo || null,
            favicon: data.favicon || null,
            domain: data.domain || "",
            subdomain: data.subdomain || "",
          });

          // Dynamically update favicon
          if (data.favicon) {
            const link =
              document.querySelector("link[rel*='icon']") ||
              document.createElement("link");
            link.type = "image/png";
            link.rel = "icon";
            link.href = getImageUrl(data.favicon);
            document.head.appendChild(link);
          }
        } else {
          setFetchError("Company info not found.");
        }
      } catch (err) {
        console.error(err);
        setFetchError("Error fetching company info.");
      }
    };

    fetchCompany();
  }, []);

  // ===== Handle form submit =====
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (email === "" || password === "") {
      toast.warn("Please enter both Email and Password.")
      setError("Please enter both Email and Password.");
      return;
    }

    try {
      const result = await dispatch(loginUser({ email, password })).unwrap();
      localStorage.setItem("authToken", result?.token);
      localStorage.setItem("userId", result?.userId);
      localStorage.setItem("isAuthenticated", "true");
      navigate("/dashboard");
    } catch (err) {
      setError("Login failed. Check your credentials.");
    }
  };

  // Extract first word of company name
  const getFirstWord = (name) => {
    if (!name) return "Your Company";
    return name.split(" ")[0];
  };

  return (
    <div className="LoginPage-container">
      <section>
        <div className="air air1"></div>
        <div className="air air2"></div>
        <div className="air air3"></div>
        <div className="air air4"></div>
      </section>

      <div className="LoginPage-card">
        {/* ===== Quote Section with Decorative Company Name (first word only) ===== */}
        <div className="LoginPage-quote">
          {/* Company Name as decorative heading */}
          <h1
            className="LoginPage-quote-title"
            style={{
              fontSize: "2.7rem",
              fontWeight: "650",
              color: "#f8f8fbff",
              letterSpacing: "1px",
              textTransform: "uppercase",
              textShadow: "2px 2px 8px rgba(0,0,0,0.1)",
              marginBottom: "0.5rem",
              transition: "all 0.3s ease-in-out",
            }}
          >
            {getFirstWord(company.name)}
          </h1>

          {/* Subheading */}
          <p
            className="LoginPage-quote-heading"
            style={{
              fontSize: "1.25rem",
              fontWeight: "500",
              color: "#fd5507ff",
              marginBottom: "1rem",
            }}
          >
            A Wise Quote
          </p>

          {/* Decorative Quote */}
          <p
            className="LoginPage-quote-text"
            style={{
              fontSize: "1rem",
              fontWeight: "400",
              fontStyle: "italic",
              color: "#f9ebebff",
              lineHeight: "1.8",
              borderLeft: "4px solid #fd5507ff",
              paddingLeft: "1rem",
              maxWidth: "400px",
            }}
          >
            You can get everything you want if you work hard, trust the process, and stick to the plan.
          </p>
        </div>

        {/* ===== Login Form Section ===== */}
        <div className="LoginPage-form">
          <div className="LoginPage-logo-container">
            {/* Dynamic Company Logo */}
            {company.logo ? (
              <img
                alt={`${company.name} logo`}
                className="LoginPage-logo"
                src={getImageUrl(company.logo)}
              />
            ) : (
              <img
                alt="Company logo"
                className="LoginPage-logo"
                src="https://storage.googleapis.com/a1aa/image/w6y3iULn6z45RKOeaslKCG7kWAkNqtUMAIBC1-6zf54.jpg"
              />
            )}

            {/* Form title */}
            <h2
              className="LoginPage-form-title"
              style={{
                fontSize: "2rem",
                fontWeight: "700",
                color: "#1E3A8A",
                marginTop: "1rem",
              }}
            >
              Welcome Back
            </h2>
            <p className="LoginPage-form-subtitle">
              Enter your email and password to access your account
            </p>
          </div>

          {/* Display errors */}
          {fetchError && <div className="LoginPage-error">⚠ {fetchError}</div>}
          {error && <div className="LoginPage-error">⚠ {error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="LoginPage-input-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                type="email"
                required
              />
            </div>

            <div className="LoginPage-input-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                type="password"
                required
              />
            </div>

            <div className="LoginPage-button-container">
              <button type="submit">Sign In</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
